// Shared listing taxonomy (frontend, additive-only). Legacy values preserved; no data migration.

// Canonical selectable amenities (order per approved spec).
export const AMENITIES = [
  "Parking", "Pool", "Fitness Gym", "Clubhouse", "Basketball Court",
  "24-Hour Security", "Wi-Fi Access", "Reception Lobby", "Playground",
  "Furnished", "Balcony", "Pet Friendly", "Garden", "Maid's Room", "Garage",
  "City View", "Concierge", "Near MRT", "Near Mall", "Corner Lot",
  "Commercial Zone", "Co-Working Space", "Near LRT", "Near Subway",
  "Near Colleges/Universities", "Entertainment Room", "Function Room", "Sky Deck / Lounge",
];

// Legacy amenity display aliases (stored value -> user-facing label). Matching is unaffected
// (engine uses bidirectional substring), so no data migration is required.
const AMENITY_ALIASES = { Gym: "Fitness Gym" };

// User-facing label for a stored amenity value.
export const amenityLabel = (v) => AMENITY_ALIASES[v] || v;

// Normalize a stored amenity list to canonical values (de-duped) for form selected-state.
export const normalizeAmenities = (list) => {
  const out = [];
  (list || []).forEach((v) => {
    if (typeof v !== "string") return;
    const c = amenityLabel(v.trim());
    if (c && !out.includes(c)) out.push(c);
  });
  return out;
};

// Property types (canonical, grouped). Legacy generic values ("Lot", "Commercial")
// are intentionally NOT selectable for new records but remain readable/recognized.
export const PROPERTY_TYPE_GROUPS = [
  { label: "Residential", items: ["Condominium", "House and Lot", "Townhouse", "Apartment / Multi-Family", "Residential Lot"] },
  { label: "Commercial", items: ["Office", "Retail / Commercial Space", "Commercial Building", "Commercial Lot", "Co-Working Space", "Virtual Office", "Events Venue"] },
  { label: "Industrial", items: ["Warehouse", "Industrial Property", "Industrial Lot"] },
  { label: "Agricultural", items: ["Farm", "Agricultural Land / Farm Lot"] },
  { label: "Hospitality", items: ["Hotel / Resort"] },
  { label: "Mixed Use", items: ["Mixed-Use Property"] },
  { label: "Other", items: ["Other"] },
];

// Stored value -> user-facing display label. Renames are presentation-only: the
// stored/canonical value (e.g. "Office") is unchanged, so matching and existing
// data are unaffected; legacy records simply render with the new label.
const PROPERTY_TYPE_LABELS = {
  Office: "Office Space",
  Other: "Other / Not Listed",
};

// User-facing label for a stored property-type value (passes through unknown/legacy
// values like "Lot"/"Commercial" so old records stay readable).
export const propertyTypeLabel = (v) => (v ? (PROPERTY_TYPE_LABELS[v] || v) : v);

// Flat ordered list of selectable canonical types (legacy values excluded).
export const PROPERTY_TYPES = PROPERTY_TYPE_GROUPS.flatMap((g) => g.items);

// --- Sale Type (Phase 1) — a classification dimension for "For Sale" listings only. ---
// Stored canonical values are the codes; forms use the fuller descriptive label, while
// cards/marketplace/badges use the shorter label for scanability. Rent/Stays get none.
// Legacy For-Sale listings may have no sale_type and simply display "FOR SALE".
export const SALE_TYPES = [
  { value: "PRIMARY", formLabel: "Developer / Pre-Selling", shortLabel: "Pre-Selling" },
  { value: "RESALE", formLabel: "Resale", shortLabel: "Resale" },
  { value: "PASALO", formLabel: "Pasalo / Assume Balance", shortLabel: "Pasalo" },
  { value: "FORECLOSED", formLabel: "Foreclosed", shortLabel: "Foreclosed" },
];

const SALE_TYPE_FORM = Object.fromEntries(SALE_TYPES.map((s) => [s.value, s.formLabel]));
const SALE_TYPE_SHORT = Object.fromEntries(SALE_TYPES.map((s) => [s.value, s.shortLabel]));

// Fuller descriptive label for the Add/Edit form (falls back to the raw value).
export const saleTypeFormLabel = (v) => (v ? (SALE_TYPE_FORM[v] || v) : v);
// Short scan-friendly label for cards / marketplace / badges.
export const saleTypeShortLabel = (v) => (v ? (SALE_TYPE_SHORT[v] || v) : v);
